// @flow
/* eslint react/no-find-dom-node: 0 */
/* eslint comma-dangle: 0 */
/* global self */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import WithLayout from 'app/common/react/components/growth/unauth/ClassicGrid/WithLayout';
import { ScrollFetch } from 'gestalt';

type Props<T> = {
  columnWidth: number,
  comp: (props: Object) => React$Element<any>,
  enableOrderedList?: boolean,
  gutterWidth: number,
  items: T[],
  minCols: number,
  loadItems: (
    ?{
      from: number,
    },
  ) => void,
  scrollContainer: HTMLElement,
};

export default class ClassicGrid extends Component<
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  Props<*>,
  {
    layoutReady: boolean,
    fetchingFrom: boolean | number,
  },
> {
  static defaultProps: {};

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  constructor(props: Props<*>) {
    super(props);

    this.currColHeights = [];
    this.setCacheKey();
    this.gridWrapperHeight = 0;

    this.state = {
      fetchingFrom: false,
      layoutReady: false,
    };
  }

  /**
   * Adds hooks after the component mounts.
   */
  componentDidMount() {
    // We calculate columns and offset after the component mounts so we can measure our container.
    this.reflow(this.calculateColumns());

    this.boundResizeHandler = () => this.handleResize();

    window.addEventListener('resize', this.boundResizeHandler);

    // Since items are positioned absolutely, we can't rely on margin or padding to center
    // an arbitrary number of columns. Calculate the width in order to center the grid.
    this.gridWrapper.style.width = this.determineWidth();

    /* eslint react/no-did-mount-set-state:0 */
    this.setState({
      layoutReady: true,
    });
  }

  // $FlowFixMe Flow v0.104.0 upgrade supression.
  UNSAFE_componentWillReceiveProps(nextProps: Props<*>) {
    if (this.state.fetchingFrom !== false && this.state.fetchingFrom !== nextProps.items.length) {
      this.setState({
        fetchingFrom: false,
      });
    }
  }

  /**
   * Sets the height of the grid after the component updates.
   * This allows stacking of items under the grid due to absolutely positioned elements.
   */
  componentDidUpdate() {
    setTimeout(() => {
      const longestColumn = Math.max.apply(null, this.currColHeights);
      if (this.gridWrapperHeight !== longestColumn) {
        this.gridWrapperHeight = longestColumn;
        this.gridWrapper.style.height = `${longestColumn}px`;
      }
    });
  }

  /**
   * Remove listeners when unmounting.
   */
  componentWillUnmount() {
    window.removeEventListener('resize', this.boundResizeHandler);
  }

  /**
   * Sets a cache key based on the number of columns and a timestamp.
   * This is consumed by the WithLayout component to cache grid item layout.
   */
  setCacheKey() {
    this.cacheKey = `${this.currColHeights.length} - ${Date.now()}`;
  }

  boundResizeHandler: () => void;
  cacheKey: string;
  currColHeights: Array<number>;
  gridWrapper: HTMLElement;
  gridWrapperHeight: number;
  resizeTimeout: ?number;

  /**
   * Delays resize handling in case the scroll container is still being resized.
   */
  handleResize() {
    if (this.resizeTimeout) {
      // $FlowFixMe: new errors found from flow 0.65 upgrade
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    // $FlowFixMe: new errors found from flow 0.65 upgrade
    this.resizeTimeout = setTimeout(this.reflowIfNeeded.bind(this), 100);
  }

  /**
   * Resets the local cache.
   */
  reflow(columnCount?: number) {
    const columnCountOutput = columnCount || this.calculateColumns();

    // Sets the columns heights as an array, each member corresponding to a column.
    if (typeof document === 'undefined') {
      this.currColHeights = [];
    } else {
      this.currColHeights = new (self.Uint32Array || self.Array)(columnCountOutput);
    }
    this.setCacheKey();

    // Whether or not we have requested new items.
    // This is used as a flag to signal that we need to wait before loading additional items.
    this.setState({
      fetchingFrom: false,
    });
    this.forceUpdate();
  }

  /**
   * Determines the number of columns to display.
   */
  calculateColumns() {
    if (!this.props.scrollContainer) {
      return 0;
    }

    const eachItemWidth = this.props.columnWidth + this.props.gutterWidth;
    /* eslint react/no-find-dom-node: 0 */
    const el = ReactDOM.findDOMNode(this);
    if (el && el.parentNode instanceof HTMLElement) {
      const parentWidth = el.parentNode.clientWidth;

      let newColCount = Math.floor(parentWidth / eachItemWidth);

      if (newColCount < this.props.minCols) {
        newColCount = this.props.minCols;
      }
      return newColCount;
    }
    throw new Error('could not calculate columns');
  }

  /**
   * Reflows items if needed after a resize.
   * We need to reflow items if the number of columns we would display should change.
   */
  reflowIfNeeded() {
    const newColCount = this.calculateColumns();
    if (newColCount !== this.currColHeights.length) {
      this.reflow(newColCount);
      this.gridWrapper.style.width = this.determineWidth();
      this.forceUpdate();
      return true;
    }
    return false;
  }

  /**
   * # of columns * total item width - 1 item margin
   */
  determineWidth() {
    const eachItemWidth = this.props.columnWidth + this.props.gutterWidth;
    return `${this.currColHeights.length * eachItemWidth - this.props.gutterWidth}px`;
  }

  fetchMore = () => {
    if (this.props.loadItems) {
      this.setState({
        fetchingFrom: this.props.items.length,
      });
      this.props.loadItems({
        from: this.props.items.length,
      });
    }
  };

  /**
   * Returns the index of the shortest column.
   */
  shortestColumn() {
    let min = 0;
    for (let i = 1; i < this.currColHeights.length; i += 1) {
      if (this.currColHeights[i] < this.currColHeights[min]) {
        min = i;
      }
    }
    return min;
  }

  /**
   * Processes height information for an item based on width and height.
   */
  processInfo = (element: HTMLElement, width: number, height: number) => {
    const column = this.shortestColumn();
    const top = this.currColHeights[column] || 0;
    const left = column * this.props.columnWidth + this.props.gutterWidth * column;
    this.currColHeights[column] += height + this.props.gutterWidth;
    /* eslint no-param-reassign: 0 */
    element.style.top = `${top}px`;
    element.style.left = `${left}px`;
    element.className = 'Grid__Item';
  };

  renderHeight = () => {
    const column = this.shortestColumn();
    return this.currColHeights[column];
  };

  render() {
    const Comp = this.props.comp;
    const enableOrderedList = this.props.enableOrderedList;

    return (
      <div
        className="Grid__Container"
        ref={(ref) => {
          // $FlowFixMe: New error after Flow Upgrade http://pinch.pinadmin.com/flow53upgdoc
          this.gridWrapper = ref;
        }}
      >
        <ScrollFetch
          container={this.props.scrollContainer}
          fetchMore={this.fetchMore}
          isFetching={this.state.fetchingFrom !== false}
          renderHeight={this.renderHeight}
        />
        <WithLayout
          data={this.props.items}
          invalidateCacheKey={this.cacheKey}
          layoutReady={this.state.layoutReady}
          enableOrderedList={enableOrderedList}
          processInfo={this.processInfo}
        >
          {!!enableOrderedList
            ? this.props.items.map((item, idx) => (
                <li className="static" style={{ listStyleType: 'none' }} key={idx}>
                  <Comp data={this.props.items[idx]} itemIdx={idx} />
                </li>
              ))
            : this.props.items.map((item, idx) => (
                <div className="static" key={idx}>
                  <Comp data={this.props.items[idx]} itemIdx={idx} />
                </div>
              ))}
        </WithLayout>
      </div>
    );
  }
}

ClassicGrid.propTypes = {
  /**
   * The width of each column.
   */
  columnWidth: PropTypes.number,

  /**
   * The component to render.
   */
  /* eslint react/no-unused-prop-types: 0 */
  comp: PropTypes.func,

  /**
   * The amount of space between each item.
   */
  gutterWidth: PropTypes.number,

  /**
   * An array of all objects to display in the grid.
   */
  items: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * A callback which the grid calls when we need to load more items as the user scrolls.
   * The callback should update the state of the items, and pass those in as props
   * to this component.
   */
  loadItems: PropTypes.func,

  /**
   * Minimum number of columns to display.
   */
  minCols: PropTypes.number,

  /**
   * The scroll container to use. Defaults to window.
   */
  scrollContainer: PropTypes.shape({
    addEventListener: PropTypes.func,
    removeEventListener: PropTypes.func,
  }),
};

ClassicGrid.defaultProps = {
  columnWidth: 236,
  gutterWidth: 14,
  minCols: 3,
  scrollContainer: typeof window !== 'undefined' ? window : null,
};
